import { useEffect, useState } from 'react';
import { LockKeyhole, PanelLeftClose, PanelLeftOpen, UnlockKeyhole } from 'lucide-react';
import { ScrollArea, Tooltip, TooltipContent, TooltipTrigger, cn } from '@helios/ui';
import { ConstructionOsLogo } from '@/components/brand/ConstructionOsLogo';
import { NavTree } from '@/components/navigation/NavTree';
import { SidebarMenuEditor } from '@/components/navigation/SidebarMenuEditor';
import { useSidebarLayoutScope, useSidebarNavGroups } from '@/navigation/useSidebarNavGroups';
import { useNavPreferences } from '@/navigation/navPreferences';
import { NAV_GROUPS } from '@/navigation/registry';
import { useScopedNavPreferences } from '@/navigation/useScopedNavPreferences';

/** Persistent left navigation (desktop). Mobile uses the MobileNav drawer. */
export function Sidebar() {
  const { sidebarCollapsed: collapsed, toggleSidebar, setSidebarCollapsed } = useScopedNavPreferences();
  const navGroups = useSidebarNavGroups();
  const scope = useSidebarLayoutScope();
  const [editingScope, setEditingScope] = useState<string | null>(null);
  const editing = !collapsed && scope !== null && editingScope === scope;
  const moveDestination = useNavPreferences((state) => state.moveSidebarDestination);
  const renameDestination = useNavPreferences((state) => state.renameSidebarDestination);
  const resetLayout = useNavPreferences((state) => state.resetSidebarLayout);

  useEffect(() => {
    // Collapsing and account changes return the rail to ordinary navigation.
    if (collapsed || editingScope !== scope) setEditingScope(null);
  }, [collapsed, editingScope, scope]);

  const configurationButton = (
    <button
      type="button"
      disabled={!scope}
      onClick={() => {
        if (editing) setEditingScope(null);
        else if (scope) {
          setSidebarCollapsed(false);
          setEditingScope(scope);
        }
      }}
      aria-label={editing ? 'Lock menu configuration' : 'Unlock menu configuration'}
      aria-pressed={editing}
      title={editing ? 'Done — lock menu configuration' : 'Unlock menu configuration'}
      className={cn(
        'flex h-10 shrink-0 items-center justify-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 text-sm font-medium text-muted-foreground transition-colors hover:border-white/20 hover:bg-white/[0.07] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
        editing && 'border-primary/50 text-primary',
      )}
    >
      {editing ? <LockKeyhole className="h-4 w-4" aria-hidden="true" /> : <UnlockKeyhole className="h-4 w-4" aria-hidden="true" />}
      {editing && <span>Done</span>}
    </button>
  );

  const collapseButton = (
    <button
      type="button"
      onClick={toggleSidebar}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!collapsed}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-white/20 hover:bg-white/[0.07] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        collapsed && 'justify-center px-0',
      )}
    >
      {collapsed ? (
        <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
      ) : (
        <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
      )}
      {!collapsed && <span>Collapse</span>}
    </button>
  );

  return (
    <aside
      aria-label="Primary"
      className={cn(
        'z-sidebar hidden h-full shrink-0 flex-col border-r border-white/[0.08] bg-popover/55 shadow-[20px_0_60px_-34px_hsl(0_0%_0%)] backdrop-blur-glass-strong transition-[width] duration-300 ease-out lg:flex',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <div className={cn('flex h-16 shrink-0 items-center justify-center gap-3 border-b border-white/[0.08] px-3', collapsed && 'gap-0 px-0')}>
        <ConstructionOsLogo variant="sidebar-toolbar" className="shrink-0" />
        {!collapsed ? (
          <div className="min-w-0 text-center leading-none">
            <p className="truncate text-[21px] font-semibold text-foreground">HELIOS</p>
            <p className="mt-1 truncate text-[15px] font-medium text-muted-foreground">ConstructionOS</p>
          </div>
        ) : null}
      </div>
      <ScrollArea className="flex-1">
        <nav aria-label="Modules" className="p-2">
          {editing && scope ? (
            <SidebarMenuEditor
              groups={navGroups}
              onMove={(destinationId, targetDomainId, beforeDestinationId) =>
                moveDestination(scope, navGroups, destinationId, targetDomainId, beforeDestinationId, NAV_GROUPS)
              }
              onRename={(destinationId, label) => renameDestination(scope, destinationId, label)}
              onReset={() => resetLayout(scope)}
            />
          ) : (
            <NavTree groups={navGroups} collapsed={collapsed} />
          )}
        </nav>
      </ScrollArea>

      <div className={cn('flex items-center gap-2 border-t border-white/[0.08] p-2', collapsed && 'flex-col')}>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>{collapseButton}</TooltipTrigger>
            <TooltipContent side="right">Expand sidebar</TooltipContent>
          </Tooltip>
        ) : (
          collapseButton
        )}
        <Tooltip>
          <TooltipTrigger asChild>{configurationButton}</TooltipTrigger>
          <TooltipContent side={collapsed ? 'right' : 'top'}>
            {editing ? 'Done — lock menu configuration' : 'Unlock menu configuration'}
          </TooltipContent>
        </Tooltip>
      </div>
    </aside>
  );
}
